import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { exploreApi, favouritesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Heart, Search, Loader2, Sparkles, Star, MapPin, BookmarkPlus, Bookmark,
  GraduationCap, Palette, Shield, Users, Globe, Briefcase, ArrowLeft, Filter
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const CATEGORY_CONFIG = {
  academic: { icon: GraduationCap, label: 'Academic', color: 'blue' },
  arts: { icon: Palette, label: 'Arts & Culture', color: 'pink' },
  military: { icon: Shield, label: 'Military & Service', color: 'green' },
  community: { icon: Users, label: 'Community', color: 'amber' },
  global: { icon: Globe, label: 'Global', color: 'violet' },
  professional: { icon: Briefcase, label: 'Professional', color: 'cyan' }
};

export default function ExplorePage() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [countries, setCountries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  useEffect(() => { loadProfiles(); }, [page, selectedCountry, selectedCategory, selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await exploreApi.search({
        search: searchQuery || undefined,
        country: selectedCountry || undefined,
        category: selectedCategory || undefined,
        profile_type: selectedType || undefined,
        page
      });
      setProfiles(data.profiles);
      setTotal(data.total);
      setPages(data.pages);
      if (data.countries) setCountries(data.countries);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCountry, selectedCategory, selectedType, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadProfiles();
  };

  const toggleFavourite = async (profileId) => {
    if (!user) { toast.error('Sign in to save favourites'); return; }
    try {
      const { data } = await favouritesApi.toggle(profileId);
      setProfiles(profiles.map(p =>
        p.id === profileId ? { ...p, is_favourited: data.is_favourited } : p
      ));
      toast.success(data.is_favourited ? 'Saved to favourites' : 'Removed from favourites');
    } catch { toast.error('Could not update favourite'); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-100/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-pink-100/40 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Heart className="w-4 h-4 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-[#1a1a2e]">iExtol</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#1a1a2e]/70 hover:text-[#1a1a2e] rounded-full" data-testid="dashboard-link">Dashboard</Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full px-6" data-testid="sign-in-button">Sign in</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1a2e] tracking-tight mb-4" data-testid="explore-heading">
            Discover Tributes
          </h1>
          <p className="text-xl text-[#1a1a2e]/50 max-w-2xl mx-auto">
            Explore stories of quiet heroes from around the world
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl mx-auto">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1a1a2e]/30" />
              <Input
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-full py-6 bg-white shadow-sm"
                placeholder="Search by name, location, or keyword..."
                data-testid="explore-search-input"
              />
            </div>
            <Button type="submit" className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full px-8 shadow-lg" data-testid="explore-search-button">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap gap-3 justify-center">
            <Select value={selectedCountry} onValueChange={(v) => { setSelectedCountry(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-48 bg-white border-[#1a1a2e]/10 rounded-full" data-testid="filter-country">
                <MapPin className="w-4 h-4 mr-2 text-[#1a1a2e]/30" />
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1a1a2e]/10">
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-48 bg-white border-[#1a1a2e]/10 rounded-full" data-testid="filter-category">
                <Filter className="w-4 h-4 mr-2 text-[#1a1a2e]/30" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1a1a2e]/10">
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedType} onValueChange={(v) => { setSelectedType(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-48 bg-white border-[#1a1a2e]/10 rounded-full" data-testid="filter-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1a1a2e]/10">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="living">Living Tributes</SelectItem>
                <SelectItem value="legacy">Legacy Tributes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-[#1a1a2e]/40 mb-6 text-center">{total} tribute{total !== 1 ? 's' : ''} found</p>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && profiles.length === 0 && (
          <div className="text-center py-24" data-testid="explore-empty">
            <Heart className="w-16 h-16 text-[#1a1a2e]/10 mx-auto mb-4" strokeWidth={1} />
            <h2 className="text-xl font-bold text-[#1a1a2e]/50 mb-2">No public tributes yet</h2>
            <p className="text-[#1a1a2e]/30">Be the first to share a tribute with the world</p>
          </div>
        )}

        {/* Grid */}
        {!loading && profiles.length > 0 && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="explore-grid">
            {profiles.map((profile) => {
              const isLiving = profile.profile_type === 'living';
              return (
                <Card key={profile.id} className="border-[#1a1a2e]/5 bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group"
                  data-testid={`explore-card-${profile.id}`}>
                  <CardContent className="p-0">
                    <Link to={`/tribute/${profile.id}`}>
                      <div className="aspect-[4/3] bg-gradient-to-br from-purple-50 to-pink-50 relative">
                        {profile.photo_url ? (
                          <img src={profile.photo_url.startsWith('http') ? profile.photo_url : `${profile.photo_url}`} alt={profile.person_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-3xl font-bold text-[#1a1a2e]/15">{profile.person_name.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <Badge className={`${isLiving ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-violet-50 text-violet-600 border-violet-200'} border rounded-full px-2 py-0.5 text-xs`}>
                            {isLiving ? <Sparkles className="w-2.5 h-2.5 mr-0.5" strokeWidth={2} /> : <Star className="w-2.5 h-2.5 mr-0.5" strokeWidth={2} />}
                            {isLiving ? 'Living' : 'Legacy'}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <Link to={`/tribute/${profile.id}`}>
                            <h3 className="text-sm font-bold text-[#1a1a2e] hover:text-[#7c3aed] transition-colors">{profile.person_name}</h3>
                          </Link>
                          {(profile.location_city || profile.location_country) && (
                            <p className="text-xs text-[#1a1a2e]/40 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-2.5 h-2.5" />
                              {[profile.location_city, profile.location_country].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                          onClick={() => toggleFavourite(profile.id)} data-testid={`fav-${profile.id}`}>
                          {profile.is_favourited
                            ? <Bookmark className="w-3.5 h-3.5 text-[#7c3aed] fill-[#7c3aed]" />
                            : <BookmarkPlus className="w-3.5 h-3.5 text-[#1a1a2e]/30" />}
                        </Button>
                      </div>
                      <p className="text-xs text-[#1a1a2e]/60 italic line-clamp-2 mb-2">"{profile.headline}"</p>
                      {profile.contribution_categories?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {profile.contribution_categories.map(cat => {
                            const config = CATEGORY_CONFIG[cat];
                            if (!config) return null;
                            const Icon = config.icon;
                            return (
                              <span key={cat} className="text-xs bg-[#1a1a2e]/5 text-[#1a1a2e]/50 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Icon className="w-3 h-3" />{config.label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-xs text-[#1a1a2e]/30 mt-3">by {profile.creator_name}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                className={p === page ? 'bg-[#7c3aed] text-white rounded-full' : 'rounded-full'}
                onClick={() => setPage(p)}>{p}</Button>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#1a1a2e] mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-white/40">&copy; {new Date().getFullYear()} Resilience Community Collective. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

